import * as React from 'react';

import type Moment from 'moment';
import moment from 'moment';
import _ from 'underscore';

import {
    checkMultiDay,
    getTimesForAllDay,
} from '../../../../../../core/date-time/activity-time-helpers';
import type {Option} from '../../../../core/select/graphql-select-inline';

import {getTimeForSelectedDate, getValidEndTime, getValidStartTime} from '../helpers';

const TIME_INCREMENT = 15;

export type VideoConferencingData = {
    meetingId: string;
    meetingUuid: string;
    meetingUrl: string;
    serviceType: 'ZOOM';
};

export type CalendarState = {
    agenda: string;
    location: string;
    startTime: number;
    endTime: number;
    isAllDay: boolean;
    isMultiDay: boolean;
    selectedParticipants: Option[];
    videoConferencingData: VideoConferencingData | null;
};

export type CalendarStateHandlers = {
    handleFormChange: () => void;
    handleStartDateChange: (newDate: number) => void;
    handleEndDateChange: (newDate: number) => void;
    // @ts-expect-error
    handleStartTimeChange: (newStartTime: Moment) => void;
    // @ts-expect-error
    handleEndTimeChange: (newEndTime: Moment) => void;
    handleAllDayChange: (newIsAllDay: boolean) => void;
    handleLocationChange: (newLocation: string) => void;
    handleSelectedParticipantsChange: (options: Option | Option[]) => void;
    handleAgendaChange: (newAgenda: string) => void;
    handleAllDay: () => void;
    setAgenda: (agenda: string) => void;
    setLocation: (location: string) => void;
    setStartTime: (startTime: number) => void;
    setEndTime: (endTime: number) => void;
    setIsAllDay: (isAllDay: boolean) => void;
    setIsMultiDay: (isMultiDay: boolean) => void;
    setSelectedParticipants: (participants: Option[]) => void;
    setVideoConferencingData: (videoConferencingData: VideoConferencingData | null) => void;
};

type UseCalendarStateOptions = {
    initialState: CalendarState;
    onFormChange?: () => void;
    onParticipantsChange?: () => void;
};

/**
 * Manages the state for calendar UI in activity modal. The handlers are
 * debounced to prevent excessive re-renders. The state is updated by the
 * handlers.
 */
export function useCalendarState(
    options: UseCalendarStateOptions
): [CalendarState, CalendarStateHandlers] {
    const {initialState, onFormChange, onParticipantsChange} = options;

    const [agenda, setAgenda] = React.useState<string>(initialState.agenda);
    const [location, setLocation] = React.useState<string>(initialState.location);
    const [startTime, setStartTime] = React.useState<number>(initialState.startTime);
    const [endTime, setEndTime] = React.useState<number>(initialState.endTime);
    const [isAllDay, setIsAllDay] = React.useState<boolean>(initialState.isAllDay);
    const [isMultiDay, setIsMultiDay] = React.useState<boolean>(initialState.isMultiDay);
    const [selectedParticipants, setSelectedParticipants] = React.useState<Option[]>(
        initialState.selectedParticipants
    );
    const [timeDuration, setTimeDuration] = React.useState<number>(endTime - startTime);
    const [videoConferencingData, setVideoConferencingData] =
        React.useState<VideoConferencingData | null>(null);

    React.useEffect(() => {
        setIsMultiDay(checkMultiDay(startTime, endTime));
    }, [startTime, endTime]);

    // If the time duration is 0, set it to the difference between the start and
    // end time if they are different
    React.useEffect(() => {
        if (startTime !== endTime && timeDuration === 0) {
            setTimeDuration(endTime - startTime);
        }
    }, [startTime, endTime, timeDuration]);

    // Debounce form changes to prevent excessive re-renders
    const debouncedFormChange = React.useMemo(
        () =>
            _.debounce(() => {
                if (onFormChange) {
                    onFormChange();
                }
            }, 200),
        [onFormChange]
    );

    const handleFormChange = React.useCallback(() => {
        debouncedFormChange();
    }, [debouncedFormChange]);

    const handleStartDateChange = React.useCallback(
        (newDate: number) => {
            const newStartTimeForSelectedDate = getTimeForSelectedDate(
                moment.unix(newDate), // Update the date
                moment.unix(startTime) // Keep the time the same
            );

            // Move the end date back based on the current time duration
            const newEndDateForSelectedDate = getValidEndTime(
                newStartTimeForSelectedDate,
                moment.unix(endTime),
                timeDuration
            );
            const newEndTimeForSelectedDate = getTimeForSelectedDate(
                newEndDateForSelectedDate,
                moment.unix(endTime)
            );

            setEndTime(newEndTimeForSelectedDate.unix());

            setStartTime(newStartTimeForSelectedDate.unix());
            handleFormChange();
        },
        [handleFormChange, startTime, endTime, timeDuration]
    );

    const handleEndDateChange = React.useCallback(
        (newDate: number) => {
            // While setting new dates, we want to preserve the pre-established
            // time
            let newEndTime = getTimeForSelectedDate(moment.unix(newDate), moment.unix(endTime));

            if (newEndTime.isBefore(moment.unix(startTime))) {
                const fixedEndTime = moment.unix(startTime).add(TIME_INCREMENT, 'm');
                // If the new date is before the start date, set the end date to 15
                // minutes after the start time. If this would push the end date
                // into the next day, set the end time to end of the day on the new
                // date.
                if (
                    fixedEndTime.isSame(moment.unix(newDate).add(1, 'day'), 'day') &&
                    fixedEndTime.isBefore(
                        moment.unix(newDate).add(1, 'day').add(TIME_INCREMENT, 'minutes')
                    )
                ) {
                    newEndTime = moment.unix(newDate).endOf('day');
                } else {
                    newEndTime = fixedEndTime;
                }
            }

            setEndTime(newEndTime.unix());

            setTimeDuration(newEndTime.diff(moment.unix(startTime), 's'));
            handleFormChange();
        },
        [handleFormChange, startTime, endTime]
    );

    const handleStartTimeChange = React.useCallback(
        // @ts-expect-error
        (newStartTime: Moment) => {
            if (!isMultiDay) {
                const newEndTime = getValidEndTime(
                    newStartTime,
                    moment.unix(endTime),
                    timeDuration
                );

                setEndTime(newEndTime.unix());

                // Set new duration in-case special conditions were met when setting
                // a valid end time
                setTimeDuration(newEndTime.diff(newStartTime, 's'));
            }
            const newStartTimeForSelectedDate = getTimeForSelectedDate(
                moment.unix(startTime), // Keep the date the same
                newStartTime // Update the time
            );

            setStartTime(newStartTimeForSelectedDate.unix());

            handleFormChange();
        },
        [handleFormChange, startTime, endTime, timeDuration, isMultiDay]
    );

    const handleEndTimeChange = React.useCallback(
        // @ts-expect-error
        (newEndTime: Moment) => {
            const newStartTime = getValidStartTime(moment.unix(startTime), newEndTime);
            const newStartTimeForSelectedDate = getTimeForSelectedDate(
                moment.unix(startTime), // Keep the date the same
                newStartTime // Update the time
            );

            setStartTime(newStartTimeForSelectedDate.unix());

            setEndTime(newEndTime.unix());
            setTimeDuration(newEndTime.diff(newStartTime, 's'));
            handleFormChange();
        },
        [handleFormChange, startTime]
    );

    const handleAllDayChange = React.useCallback(
        (newIsAllDay: boolean) => {
            setIsAllDay(newIsAllDay);
            handleFormChange();
        },
        [handleFormChange]
    );

    const handleLocationChange = React.useCallback(
        (newLocation: string) => {
            setLocation(newLocation);
            handleFormChange();
        },
        [handleFormChange]
    );

    const handleSelectedParticipantsChange = React.useCallback(
        (newOptions: Option | Option[], preventFromStatusChange?: boolean) => {
            if (Array.isArray(newOptions)) {
                setSelectedParticipants(newOptions);
            } else {
                setSelectedParticipants([newOptions]);
            }
            if (onParticipantsChange) {
                onParticipantsChange();
            }
            if (preventFromStatusChange === true) {
                return;
            }
            handleFormChange();
        },
        [handleFormChange, onParticipantsChange]
    );

    const handleAgendaChange = React.useCallback(
        (newAgenda: string) => {
            setAgenda(newAgenda);
            handleFormChange();
        },
        [handleFormChange]
    );

    // Used on tab save to ensure the times stored in state are reset to match
    // the full day duration if the activity is all day
    const handleAllDay = React.useCallback(() => {
        if (isAllDay) {
            const allDayTimes = getTimesForAllDay(startTime, endTime);
            setStartTime(allDayTimes.startTime);
            setEndTime(allDayTimes.endTime);
            setTimeDuration(allDayTimes.endTime - allDayTimes.startTime);
        }
    }, [startTime, endTime, isAllDay]);

    const state: CalendarState = {
        agenda,
        location,
        startTime,
        endTime,
        isAllDay,
        isMultiDay,
        selectedParticipants,
        videoConferencingData,
    };

    const handlers: CalendarStateHandlers = {
        handleFormChange,
        handleStartDateChange,
        handleEndDateChange,
        handleStartTimeChange,
        handleEndTimeChange,
        handleAllDayChange,
        handleLocationChange,
        handleSelectedParticipantsChange,
        handleAgendaChange,
        handleAllDay,
        setAgenda,
        setLocation,
        setStartTime,
        setEndTime,
        setIsAllDay,
        setIsMultiDay,
        setSelectedParticipants,
        setVideoConferencingData,
    };

    return [state, handlers];
}
